import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, Autocomplete, TextField, Button, Chip,
  CircularProgress, Snackbar, Alert, Grid, ToggleButton, ToggleButtonGroup,
  FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SyncIcon from '@mui/icons-material/Sync';
import ResponsiveTable from '../components/common/ResponsiveTable';
import apiService from '../services/api.service';

interface CustomerOpt { cardCode: string; cardName: string; }
/** Which date the aging is measured from. Mirrors SAP's aging report. */
type AgingBasis = 'invoice' | 'due';
type Bucket = 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd91_120' | 'd120plus';
interface SoaInvoice {
  docNum: number; docDate: string; dueDate: string; daysOverdue: number;
  paymentStatus: 'Unpaid' | 'Partial' | 'Paid'; docTotal: number; paidToDate: number; openAmount: number;
}
interface SoaData {
  cardCode: string; cardName: string; currency?: string; asOf: string;
  source?: 'local' | 'live';
  agingBasis?: AgingBasis;
  consolidated?: boolean;
  members?: { cardCode: string; cardName: string }[];
  /** True when the caller may consolidate (sales.soa.view_all). */
  canConsolidate?: boolean;
  invoices: SoaInvoice[]; totals: Record<Bucket, number>; totalOpen: number;
  /** Statement period actually applied (YYYY-MM-DD), echoed by the server. */
  periodFrom?: string;
  periodTo?: string;
  /** Still-open total of invoices dated before periodFrom, and how many. */
  broughtForward?: number;
  broughtForwardCount?: number;
  /** Invoices hidden for being dated after periodTo. */
  excludedAfterTo?: number;
  /** Decimal places for every amount, from the company's setting (KWD 3). */
  amountDecimals?: number;
}

const BUCKETS: { key: Bucket; label: string }[] = [
  { key: 'current', label: 'Current' },
  { key: 'd1_30', label: '1-30' },
  { key: 'd31_60', label: '31-60' },
  { key: 'd61_90', label: '61-90' },
  { key: 'd91_120', label: '91-120' },
  { key: 'd120plus', label: '120+' },
];

/** Amounts with the company's decimal places (Company Management; KWD 3, others 2). */
const fmtMoney = (n: number, decimals = 2) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const fmtDate = (s: string) => (s ? new Date(s).toLocaleDateString() : '—');

const STATUS_COLOR: Record<SoaInvoice['paymentStatus'], 'default' | 'warning' | 'success'> = {
  Unpaid: 'default', Partial: 'warning', Paid: 'success',
};

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  // Defer revoke so the download has started — immediate revoke races on
  // mobile Safari / older Chromium (matches PaymentCertificate's approach).
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function CustomerSOA() {
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || undefined, []);
  const [options, setOptions] = useState<CustomerOpt[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<CustomerOpt | null>(null);
  const [soa, setSoa] = useState<SoaData | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Invoice date is the default, matching how the statement is normally read:
  // how long the money has been outstanding, not how far past terms it is.
  const [basis, setBasis] = useState<AgingBasis>('invoice');
  // Off by default: a statement covering sibling accounts is a deliberate choice,
  // not something to spring on someone who asked for one customer.
  const [consolidated, setConsolidated] = useState(false);
  // Statement period. Empty = unbounded, which is the pre-existing behaviour:
  // every open invoice itemised, nothing brought forward.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const notify = useCallback((msg: string, sev: 'success' | 'error' = 'error') => setToast({ msg, sev }), []);
  const money = (n: number) => fmtMoney(n, soa?.amountDecimals ?? 2);

  // Debounced customer search (assigned list for salespeople, SAP search for managers).
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res: any = await apiService.getSoaCustomers(companyId, search.trim() || undefined);
        if (!cancelled) setOptions(res.data || []);
      } catch { if (!cancelled) setOptions([]); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, companyId]);

  const loadSoa = useCallback(async (
    cardCode: string, live = false, forBasis?: AgingBasis, forConsolidated?: boolean,
    forPeriod?: { fromDate?: string; toDate?: string },
  ) => {
    if (!companyId) return;
    const period = forPeriod ?? { fromDate, toDate };
    // Caught here so the user is told which way round the dates go, rather than
    // the server rejecting it after a round-trip.
    if (period.fromDate && period.toDate && period.fromDate > period.toDate) {
      notify('From Date must be on or before To Date');
      return;
    }
    setLoading(true); setSoa(null);
    try {
      const res: any = await apiService.getCustomerSoa(
        companyId, cardCode, live, forBasis ?? basis, forConsolidated ?? consolidated, period,
      );
      setSoa(res.data);
    } catch (e: any) {
      notify(e.response?.data?.message || 'Failed to load SOA');
    } finally {
      setLoading(false);
    }
  }, [companyId, notify, basis, consolidated, fromDate, toDate]);

  const downloadPdf = async () => {
    if (!companyId || !soa) return;
    setDownloading(true);
    try {
      // soa.source is what is on screen right now: 'live' after "Get live from
      // SAP B1", 'local' otherwise. Passing it keeps the PDF and the page in step.
      const blob = await apiService.getCustomerSoaPdf(
        companyId, soa.cardCode, soa.agingBasis ?? basis, soa.source === 'live',
        soa.consolidated ?? consolidated,
        // The period the SERVER applied, not what is currently typed in the
        // boxes — otherwise editing a date without re-running the statement
        // would export a document that disagrees with the screen.
        { fromDate: soa.periodFrom, toDate: soa.periodTo },
      );
      downloadBlob(blob, `SOA_${soa.cardCode}.pdf`);
    } catch (e: any) {
      notify(e.response?.data?.message || 'PDF download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!companyId) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="info">Select a company (top bar) to view customer statements.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" component="h1" gutterBottom>Customer Statement of Account</Typography>

      <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 280 }}
            size="small"
            options={options}
            value={selected}
            onChange={(_, v) => { setSelected(v); if (v) void loadSoa(v.cardCode); else setSoa(null); }}
            onInputChange={(_, v) => setSearch(v)}
            getOptionLabel={(o) => `${o.cardName} (${o.cardCode})`}
            isOptionEqualToValue={(a, b) => a.cardCode === b.cardCode}
            renderInput={(params) => <TextField {...params} label="Select customer" />}
          />
          <TextField
            size="small"
            type="date"
            label="From Date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: toDate || undefined }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            type="date"
            label="To Date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: fromDate || undefined }}
            sx={{ minWidth: 160 }}
          />
          <Button
            variant="outlined"
            onClick={() => selected && void loadSoa(selected.cardCode)}
            disabled={!selected || loading}
          >
            Apply
          </Button>
          {(fromDate || toDate) && (
            <Button
              size="small"
              onClick={() => {
                setFromDate(''); setToDate('');
                // Pass explicitly — setState has not applied yet here.
                if (selected) void loadSoa(selected.cardCode, false, undefined, undefined, {});
              }}
              disabled={loading}
            >
              Clear dates
            </Button>
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={basis}
            onChange={(_, v: AgingBasis | null) => {
              // exclusive ToggleButtonGroup emits null when the active button is
              // re-clicked; ignore that so the aging always has a basis.
              if (!v || v === basis) return;
              setBasis(v);
              // Pass the new basis explicitly — setState has not applied yet here.
              if (selected) void loadSoa(selected.cardCode, false, v);
            }}
            aria-label="Age the statement by"
          >
            <ToggleButton value="invoice" aria-label="Age by invoice date">Invoice date</ToggleButton>
            <ToggleButton value="due" aria-label="Age by due date">Due date</ToggleButton>
          </ToggleButtonGroup>
          {/* Only rendered for a caller the server would honour it for, so the
              control is never present-but-ignored. */}
          {soa?.canConsolidate && (
            <Tooltip title="Include the mother account and all of its child accounts in one statement">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={consolidated}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setConsolidated(v);
                      // Pass explicitly — setState has not applied yet here.
                      if (selected) void loadSoa(selected.cardCode, false, undefined, v);
                    }}
                  />
                }
                label="Consolidated"
              />
            </Tooltip>
          )}
          <Button
            variant="contained" startIcon={<SyncIcon />}
            onClick={() => selected && void loadSoa(selected.cardCode, true)} disabled={!selected || loading}
          >
            Get live from SAP B1
          </Button>
          <Button
            variant="outlined" startIcon={<PictureAsPdfIcon />}
            onClick={() => void downloadPdf()} disabled={!soa || downloading}
          >
            {downloading ? 'Preparing…' : 'Download PDF'}
          </Button>
        </Stack>
      </Paper>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && soa && (
        <>
          {/* Say plainly which accounts the figures cover — a consolidated
              balance that looks like one customer's is materially misleading. */}
          {soa.consolidated && soa.members && soa.members.length > 1 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Consolidated across {soa.members.length} accounts under{' '}
              <strong>{soa.cardName} ({soa.cardCode})</strong>:{' '}
              {soa.members.map((m) => m.cardCode).join(', ')}
            </Alert>
          )}
          {consolidated && soa.canConsolidate && !soa.consolidated && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This customer has no child accounts — showing its own statement.
            </Alert>
          )}

          {/* The To Date removing rows must be visible. A statement that is
              quietly short of invoices reads as a customer who owes less. */}
          {!!soa.excludedAfterTo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {soa.excludedAfterTo} invoice{soa.excludedAfterTo === 1 ? '' : 's'} dated after{' '}
              {fmtDate(soa.periodTo || '')} {soa.excludedAfterTo === 1 ? 'is' : 'are'} excluded from this statement.
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {BUCKETS.map((b) => (
              <Grid item xs={6} sm={4} md={2} key={b.key}>
                <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">{b.label}</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{money(soa.totals[b.key])}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle2" color="text.secondary">
                {soa.cardName} ({soa.cardCode}) — as of {fmtDate(soa.asOf)}
                {(soa.periodFrom || soa.periodTo) && (
                  <> · Period {soa.periodFrom ? fmtDate(soa.periodFrom) : 'Opening'} to{' '}
                  {soa.periodTo ? fmtDate(soa.periodTo) : 'Latest'}</>
                )}
              </Typography>
              <Chip
                size="small" variant="outlined"
                label={soa.source === 'live' ? 'Live from SAP B1' : 'Synced data'}
                color={soa.source === 'live' ? 'success' : 'default'}
              />
            </Stack>
            <Typography variant="h6">Total Outstanding: {money(soa.totalOpen)} {soa.currency || ''}</Typography>
          </Paper>

          {/* Opening line, shown whenever a From Date is set — including at zero,
              so "nothing outstanding before this period" is distinguishable
              from a missing opening balance. */}
          {soa.periodFrom && (
            <Paper
              elevation={0}
              sx={{ p: 1.5, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'action.hover' }}
            >
              <Typography variant="subtitle2">
                Balance brought forward (before {fmtDate(soa.periodFrom)})
                {!!soa.broughtForwardCount && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}— {soa.broughtForwardCount} invoice{soa.broughtForwardCount === 1 ? '' : 's'}
                  </Typography>
                )}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {money(soa.broughtForward ?? 0)} {soa.currency || ''}
              </Typography>
            </Paper>
          )}

          <ResponsiveTable
            ariaLabel="Open invoices"
            rows={soa.invoices}
            getRowKey={(r) => r.docNum}
            emptyMessage="No open invoices for this customer."
            columns={[
              { id: 'docNum', header: 'Invoice', cell: (r) => r.docNum },
              { id: 'docDate', header: 'Date', cell: (r) => fmtDate(r.docDate) },
              { id: 'dueDate', header: 'Due Date', cell: (r) => fmtDate(r.dueDate) },
              { id: 'daysOverdue', header: 'Days', align: 'right', cell: (r) => (r.daysOverdue > 0 ? r.daysOverdue : '—') },
              { id: 'status', header: 'Status', cell: (r) => <Chip size="small" label={r.paymentStatus} color={STATUS_COLOR[r.paymentStatus]} /> },
              { id: 'docTotal', header: 'Total', align: 'right', cell: (r) => money(r.docTotal) },
              { id: 'paidToDate', header: 'Paid', align: 'right', cell: (r) => money(r.paidToDate) },
              { id: 'openAmount', header: 'Open', align: 'right', cell: (r) => <strong>{money(r.openAmount)}</strong> },
            ]}
          />

          {/* Closing balance: brought forward + what is listed above. Stated
              explicitly on a dated statement so the arithmetic is checkable. */}
          {soa.periodFrom && (
            <Paper
              elevation={0}
              sx={{ p: 1.5, mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="subtitle2">Closing balance (brought forward + listed)</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {money(soa.totalOpen)} {soa.currency || ''}
              </Typography>
            </Paper>
          )}
        </>
      )}

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default CustomerSOA;
